import * as React from 'react';

import { ReactEChartsCore } from '../../../common/charts/echarts-core';
import { useLocalStorage } from '../../../hooks/use-local-storage';
import { ChartData } from '../agent-types';

const defaultHorizontalChartHeight = 420;
const minHorizontalChartHeight = 300;
const maxHorizontalChartHeight = 1200;
const singleHorizontalCategoryHeight = 60;
const groupedHorizontalBaseHeight = 50;
const groupedHorizontalSeriesHeightIncrement = 25;
const chartSizeStorageKey = 'agent-results-chart-size';

interface StoredChartSize {
    width: number;
    height: number;
}

const getDefaultChartHeight = (chart: ChartData) => {
    if (chart.type !== 'horizontal-bar') {
        return defaultHorizontalChartHeight;
    }

    const categories = chart.options?.categories || [];
    const seriesCount = chart.series.length;
    const isStacked = chart.options?.stacked ?? false;

    const heightPerCategory =
        isStacked || seriesCount === 1
            ? singleHorizontalCategoryHeight
            : groupedHorizontalBaseHeight + seriesCount * groupedHorizontalSeriesHeightIncrement;

    const calculatedHeight = categories.length * heightPerCategory;
    return Math.max(minHorizontalChartHeight, Math.min(maxHorizontalChartHeight, calculatedHeight));
};

export const useChartContainerSize = (chart: ChartData, chartRef: React.MutableRefObject<ReactEChartsCore | null>) => {
    const containerRef = React.useRef<HTMLDivElement>(null);
    const defaultHeight = React.useMemo(() => getDefaultChartHeight(chart), [chart]);
    const [storedSize, setStoredSize] = useLocalStorage<StoredChartSize | null>(chartSizeStorageKey, null);

    React.useEffect(() => {
        const container = containerRef.current;
        if (!container) {
            return;
        }

        const applySize = (size: { width?: number; height: number }) => {
            if (typeof size.width === 'number' && size.width > 0) {
                container.style.width = `${size.width}px`;
            } else {
                container.style.width = '';
            }
            container.style.height = `${size.height}px`;
        };

        const isWithinViewport = (size: StoredChartSize | null) => {
            if (!size || typeof window === 'undefined') {
                return false;
            }
            const viewportWidth = window.innerWidth;
            const viewportHeight = window.innerHeight;
            return size.width > 0 && size.height > 0 && size.width < viewportWidth && size.height < viewportHeight;
        };

        if (isWithinViewport(storedSize)) {
            applySize(storedSize);
        } else {
            applySize({ height: defaultHeight });
            if (storedSize) {
                setStoredSize(null);
            }
        }

        const resizeObserver = new ResizeObserver((entries) => {
            const entry = entries[0];
            if (!entry) {
                return;
            }
            const { width, height } = entry.contentRect;
            if (width <= 0 || height <= 0) {
                return;
            }

            const instance = chartRef.current?.getEchartsInstance();
            instance?.resize();

            if (typeof window === 'undefined') {
                return;
            }
            const viewportWidth = window.innerWidth;
            const viewportHeight = window.innerHeight;
            if (width >= viewportWidth || height >= viewportHeight) {
                applySize({ height: defaultHeight });
                if (storedSize) {
                    setStoredSize(null);
                }
                return;
            }

            const roundedWidth = Math.round(width);
            const roundedHeight = Math.round(height);
            if (!storedSize || storedSize.width !== roundedWidth || storedSize.height !== roundedHeight) {
                setStoredSize({ width: roundedWidth, height: roundedHeight });
            }
        });

        resizeObserver.observe(container);

        const handleWindowResize = () => {
            if (!containerRef.current) {
                return;
            }
            const rect = containerRef.current.getBoundingClientRect();
            const viewportWidth = window.innerWidth;
            const viewportHeight = window.innerHeight;
            if (rect.width > viewportWidth || rect.height > viewportHeight) {
                applySize({ height: defaultHeight });
                setStoredSize(null);
            }
            const instance = chartRef.current?.getEchartsInstance();
            instance?.resize();
        };

        window.addEventListener('resize', handleWindowResize);

        return () => {
            resizeObserver.disconnect();
            window.removeEventListener('resize', handleWindowResize);
        };
    }, [chartRef, defaultHeight, setStoredSize, storedSize]);

    return containerRef;
};
